import React from 'react';

interface SectionShellProps {
 step: number;          // 1-based step index
 total: number;         // total steps
 title: string;
 purpose: string;
 icon: React.ElementType;
 /** Optional right-aligned content in the header (counts, actions). */
 right?: React.ReactNode;
 /** When true, constrains content to a readable centered column (for reading sections). */
 reading?: boolean;
 children: React.ReactNode;
}

/**
 * SectionShell — the unified frame for every LLD workspace section.
 * Provides a consistent step header (step indicator + icon + title + purpose)
 * and a framed, scrollable body so all 8 sections share the same structure.
 */
const SectionShell: React.FC<SectionShellProps> = ({
 step,
 total,
 title,
 purpose,
 icon: Icon,
 right,
 reading = false,
 children,
}) => {
 return (
  <div className="lld-section-shell flex flex-col h-full bg-gray-50/60 dark:bg-gray-950/40">
   {/* Header */}
   <header className="flex-shrink-0 px-6 py-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
    <div className="flex items-center gap-3">
     <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
     </div>
     <div className="min-w-0">
      <div className="flex items-center gap-2">
       <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-600/80 dark:text-primary-400/80">
        Step {step} of {total}
       </span>
      </div>
      <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
       {title}
      </h2>
     </div>
     {right && <div className="ml-auto flex-shrink-0">{right}</div>}
    </div>
    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
     {purpose}
    </p>
   </header>

   {/* Body */}
   <div className="flex-1 overflow-y-auto">
    {reading ? (
     <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8">{children}</div>
    ) : (
     <div className="h-full p-4 sm:p-6">{children}</div>
    )}
   </div>
  </div>
 );
};

export default SectionShell;
